import { memo, useMemo, useEffect, useState, useCallback } from "react";
import {
  getPostsAction,
  clearPostsAction,
  getSkillsFeedPostsAction,
} from "../../redux/actions/postActions";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import Post from "../post/Post";
import PostForm from "../form/PostForm";
import PostSkeleton from "../loader/PostSkeleton";
import Home from "../../assets/home.jpg";
import LoadMoreButton from "../shared/LoadMoreButton";

const MemoizedPost = memo(Post);

const PostSkeletonLoader = () => (
  <div className="w-full space-y-3 flex flex-col">
    {[...Array(3)].map((_, index) => (
      <PostSkeleton key={index} />
    ))}
  </div>
);

const MainSection = ({ userData }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadMoreLoading, setIsLoadMoreLoading] = useState(false);

  // Check if we're on the skills feed page (including root path)
  const isSkillsFeed = location.pathname === "/skills-feed" || location.pathname === "/";

  // Get the appropriate posts based on the page
  const posts = useSelector((state) => 
    isSkillsFeed ? state.posts?.skillsFeedPosts : state.posts?.posts
  );
  const totalPosts = useSelector((state) => 
    isSkillsFeed ? state.posts?.totalSkillsFeedPosts : state.posts?.totalPosts
  );

  const LIMIT = 10;


  useEffect(() => {
    if (userData) {
      const fetchPosts = async () => {
        setIsLoading(true);
        if (isSkillsFeed) {
          await dispatch(getSkillsFeedPostsAction(LIMIT, 0));
        } else {
          await dispatch(getPostsAction(LIMIT, 0));
        }
        setIsLoading(false);
      };
      fetchPosts();
    }

    return () => {
      dispatch(clearPostsAction());
    };
  }, [userData, dispatch, LIMIT, isSkillsFeed]);

  const handleLoadMore = useCallback(() => {
    setIsLoadMoreLoading(true);
    const action = isSkillsFeed ? getSkillsFeedPostsAction : getPostsAction;
    dispatch(action(LIMIT, posts.length)).finally(() => {
      setIsLoadMoreLoading(false);
    });
  }, [dispatch, LIMIT, posts.length, isSkillsFeed]);

  const memoizedPosts = useMemo(() => {
    return posts.map((post) => <MemoizedPost key={post._id} post={post} />);
  }, [posts]);

  // Handle scrolling to a specific post when navigating back from post detail
  useEffect(() => {
    if (location.state?.scrollToPost && !isLoading && posts.length > 0) {
      const postId = location.state.scrollToPost;
      // Use setTimeout to ensure DOM is fully rendered
      const scrollTimer = setTimeout(() => {
        const postElement = document.getElementById(`post-${postId}`);
        if (postElement) {
          postElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Clear the state to prevent re-scrolling on re-renders
          window.history.replaceState({ ...location.state, scrollToPost: undefined }, '');
        }
      }, 200);
      return () => clearTimeout(scrollTimer);
    }
  }, [location.state, isLoading, posts.length]);

  return (
    <div className="w-full max-w-3xl mx-auto flex flex-col gap-4">
      <PostForm communityId={null} communityName="General" />
      
      {isLoading ? (
        <PostSkeletonLoader />
      ) : (
        <>
          <div className="w-full space-y-3 flex flex-col">
            {memoizedPosts}
            {posts.length === 0 && (
              <div className="w-full bg-white border rounded-lg p-8 text-center">
                <h3 className="text-lg font-semibold text-gray-700 mb-2">No posts to show</h3>
                <p className="text-gray-500 mb-4 font-roboto-flex">Join a community and post something.</p>
                <img loading="lazy" src={Home} alt="no post" className="mx-auto max-w-xs" />
              </div>
            )}
          </div>

          {posts.length >= LIMIT && posts.length < totalPosts && (
            <LoadMoreButton
              onClick={handleLoadMore}
              isLoading={isLoadMoreLoading}
              text="Load More Posts"
            />
          )}
        </>
      )}
    </div>
  );
};

export default MainSection;
